// Renders an AI-generated quote — scope, line items, total, lead time.
// After acceptance, shows an inline scheduling widget; after booking, shows
// the confirmed slot and vendor contact.

import { useState } from "react";
import RegenerateBar from "../components/RegenerateBar";

const fmtMoney = (n) => n == null ? "—" : `$${Math.round(n).toLocaleString()}`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const TIME_SLOTS = [
  { id: "morning",   label: "Morning",   sub: "8 am – 12 pm", hour: 9  },
  { id: "afternoon", label: "Afternoon", sub: "12 – 5 pm",    hour: 13 },
  { id: "evening",   label: "Evening",   sub: "5 – 8 pm",     hour: 17 },
];

// Generate next `count` weekdays starting from tomorrow.
function getWeekdays(count = 14) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function fmtConfirmed(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const slot = TIME_SLOTS.find((s) => s.hour === d.getHours()) || TIME_SLOTS.find((s) => d.getHours() < s.hour + 4);
  return `${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()} · ${slot?.label || d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

function SchedulePicker({ quoteId, onBooked }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const weekdays = getWeekdays(14);

  const confirm = async () => {
    if (!selectedDate || !selectedSlot) return;
    setBusy(true); setErr(null);
    const d = new Date(selectedDate);
    d.setHours(selectedSlot.hour, 0, 0, 0);
    try {
      await onBooked(d.toISOString(), notes);
    } catch (e) {
      setErr(e.message || "Booking failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: "14px 16px", background: "#f2f8f5", border: "1px solid #c4e8d8", borderRadius: 12 }}>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#2d7a62", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
        Schedule appointment
      </div>

      {/* Date grid */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#5b7365", marginBottom: 8 }}>Pick a date</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {weekdays.map((d) => {
            const key = d.toDateString();
            const isSel = selectedDate?.toDateString() === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedDate(d)}
                style={{
                  background: isSel ? "#1c5448" : "#fff",
                  color: isSel ? "#7ecfb0" : "#1c2b25",
                  border: `1px solid ${isSel ? "#1c5448" : "#d8ede6"}`,
                  borderRadius: 8,
                  padding: "6px 10px",
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "'DM Mono',monospace",
                  lineHeight: 1.3,
                  textAlign: "center",
                  minWidth: 48,
                }}
              >
                <div style={{ fontSize: 10, opacity: 0.7 }}>{DAYS[d.getDay()]}</div>
                <div>{MONTHS[d.getMonth()]} {d.getDate()}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#5b7365", marginBottom: 8 }}>Pick a time</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {TIME_SLOTS.map((s) => {
            const isSel = selectedSlot?.id === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedSlot(s)}
                style={{
                  flex: 1,
                  background: isSel ? "#1c5448" : "#fff",
                  color: isSel ? "#7ecfb0" : "#1c2b25",
                  border: `1px solid ${isSel ? "#1c5448" : "#d8ede6"}`,
                  borderRadius: 10,
                  padding: "10px 8px",
                  cursor: "pointer",
                  textAlign: "center",
                  minWidth: 90,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{s.label}</div>
                <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", opacity: 0.7 }}>{s.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional notes */}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Access notes for the vendor (optional) — gate code, parking, contact on site…"
        style={{
          width: "100%", background: "#fff", border: "1px solid #d8ede6", color: "#1c2b25",
          borderRadius: 8, padding: "8px 12px", fontSize: 12, fontFamily: "inherit",
          resize: "vertical", minHeight: 52, marginBottom: 12, boxSizing: "border-box",
        }}
      />

      {err && <div style={{ color: "#c0354a", fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <button
        type="button"
        onClick={confirm}
        disabled={!selectedDate || !selectedSlot || busy}
        style={{
          background: selectedDate && selectedSlot ? "#1c5448" : "#d8ede6",
          color: selectedDate && selectedSlot ? "#7ecfb0" : "#9bbfb2",
          border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 13,
          fontWeight: 600, cursor: selectedDate && selectedSlot ? "pointer" : "not-allowed",
          fontFamily: "inherit", width: "100%",
        }}
      >
        {busy ? "Confirming…" : selectedDate && selectedSlot
          ? `Confirm — ${DAYS[selectedDate.getDay()]} ${MONTHS[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedSlot.label}`
          : "Select a date and time"}
      </button>
    </div>
  );
}

export default function QuoteCard({ quote, vendorName, onAccept, onRegenerate, accepted, vendorContact, quoteId, scheduledAt, onSchedule }) {
  if (!quote) return null;
  return (
    <div style={{
      background: "linear-gradient(135deg,#f7faf8,#eef0ff)",
      border: "1px solid #d0d2ff",
      borderRadius: 14, padding: 18, marginTop: 14, marginBottom: 8,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
        <div className="s-ai-pill" style={{ margin: 0 }}>★ AI quote · {vendorName || "vendor"}</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: "#1c5448" }}>
          {fmtMoney(quote.total)}
        </div>
      </div>

      {quote.scope && (
        <div style={{ fontSize: 14, color: "#1c2b25", lineHeight: 1.6, marginBottom: 12 }}>
          {quote.scope}
        </div>
      )}

      {quote.line_items?.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e1ede7", borderRadius: 8, padding: 10, marginBottom: 12 }}>
          {quote.line_items.map((li, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: i < quote.line_items.length - 1 ? "1px solid #f0f4f2" : "none", fontSize: 13 }}>
              <div style={{ color: "#1c2b25" }}>
                {li.label}
                {li.qty > 1 && <span style={{ color: "#9aa9a0", marginLeft: 6, fontFamily: "'DM Mono',monospace", fontSize: 11 }}>× {li.qty}</span>}
              </div>
              <div style={{ fontFamily: "'DM Mono',monospace", color: "#5b7365", flexShrink: 0 }}>{fmtMoney(li.total)}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#5b7365", letterSpacing: "0.06em" }}>
          {quote.lead_time_days != null && `${quote.lead_time_days}-day lead`}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {onAccept && !accepted && (
            <button type="button" className="s-btn primary" onClick={onAccept} style={{ padding: "8px 14px", fontSize: 12 }}>
              Accept quote →
            </button>
          )}
          {accepted && !scheduledAt && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#2d7a62", letterSpacing: "0.06em" }}>
              ✓ accepted
            </span>
          )}
          {accepted && scheduledAt && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#2d7a62", letterSpacing: "0.06em" }}>
              ✓ booked · {fmtConfirmed(scheduledAt)}
            </span>
          )}
        </div>
      </div>

      {onRegenerate && !accepted && (
        <RegenerateBar
          onRegenerate={onRegenerate}
          loading={false}
          placeholder="e.g. can you do it faster, lower cost…"
        />
      )}

      {quote.rationale && (
        <div style={{ fontSize: 11, color: "#9aa9a0", marginTop: 12, fontStyle: "italic", lineHeight: 1.5 }}>
          {quote.rationale}
        </div>
      )}

      {/* Scheduling widget — shown after acceptance until booked */}
      {accepted && !scheduledAt && onSchedule && (
        <SchedulePicker quoteId={quoteId} onBooked={onSchedule} />
      )}

      {/* Vendor contact — shown after booking */}
      {accepted && scheduledAt && vendorContact && (vendorContact.email || vendorContact.phone) && (
        <div style={{ marginTop: 14, padding: "12px 14px", background: "#e8f7f0", border: "1px solid #b4dfc8", borderRadius: 10 }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#2d7a62", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
            Vendor contact
          </div>
          {vendorContact.name && (
            <div style={{ fontWeight: 600, fontSize: 14, color: "#152d21", marginBottom: 4 }}>{vendorContact.name}</div>
          )}
          {vendorContact.email && (
            <div style={{ fontSize: 13, color: "#1c5448" }}>
              <a href={`mailto:${vendorContact.email}`} style={{ color: "#1c5448", textDecoration: "none" }}>
                {vendorContact.email}
              </a>
            </div>
          )}
          {vendorContact.phone && (
            <div style={{ fontSize: 13, color: "#1c5448", marginTop: 3 }}>
              <a href={`tel:${vendorContact.phone}`} style={{ color: "#1c5448", textDecoration: "none" }}>
                {vendorContact.phone}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
